import { NullableStringOrNumber } from './types';

export interface Template {
    id?: string;
    nombre: string;
    idUsuario: string;
    mapping: Mapping;
    defecto: boolean;
    qm: boolean;
    enablePalletOption?: boolean;
    enablePackageOption?: boolean;
}

export interface MappingBooleans {
    codPedidoAutogenerar: boolean;
    pedidoOpFijar: boolean;
    clienteCodigoAutogenerar: boolean;
    clienteCodigoTrim: boolean;
    fechaPedidoAutogenerar: boolean;
    demoraDuracionFijar: boolean;
    ordenEntregaPorFechaHora?: boolean;
}

export interface MappingDefaults {
    pedidoOp: 'E' | 'R' | '';
    demoraDuracionDefault: string;
    fechaFormato?: string;
}

export interface MappingIndexes {
    primerFilaDatos: string;
    ultimaColDatos: string;
    clienteCodigo: string;
    clienteRazon: string;
    telefono: string;
    email: string;
    fechaPedido: string;
    latitud: string;
    longitud: string;
    pedidoOpMapeo: string; // Columna del tipo de operación
    codPedido: string;
    codProducto: string;
    descProducto: string;
    cantProducto: string;
    peso: string;
    volumen: string;
    dinero: string;
    demoraDuracion: string;
    horarioAm: string; //Ventana horaria 1
    horarioPm: string; //Ventana horaria 2
    comentario: string; //Notas
    agrupacion: string;
    merchantEmailList: string;
    habilidades: string;
    abasto: string;
    marca: string;
    direccion: string;
    direccion2: string; // Dirección 2 (piso, depto, torre). No se geocodifica
    codigoPostal: string;
    vehiculo: string;
    vehiculoDireccion: string;
    ungroup: string;
    ordenEntrega: string;
    ordenEntregaFormato: string;
    promocion: string;
    retornable: string;
    pallet: string;
    bultos: string;
}

export interface Mapping extends MappingBooleans, MappingDefaults, MappingIndexes {}

export interface TiendaNubeToken {
    access_token: string;
    scope: string;
    token_type: string;
    user_id: number;
}

export interface Carrier {
    value: string;
    label: string;
}

type AmountOfOrders = number;

type SuccesInfo = {
    clientes_creados?: { cantidad: number };
    sinCliente?: { cantidad: number };
    sinGeo?: { cantidad: number };
    duplicados?: { cantidad: number };
    modificados?: { cantidad: number };
    sinArticulo?: { cantidad: number };
    logFile?: { link: string };
    borrados?: { cantidad: number };
    tiempo_ejecucion?: { cantidad: number };
    cliente_modificados?: { cantidad: number };
    cliente_borrados?: { cantidad: number };
    productos_creados?: { cantidad: number };
    productos_modificados?: { cantidad: number };
    pedidos_actualizados?: { cantidad: number };
    pedidos?: { cantidad: number };
};

export interface IWarehouses {
    id: number;
    codigo_erp?: string;
    domicilio?: string;
    descripcion?: string;
    latitud?: string;
    longitud?: string;
}

export interface IDevices {
    id_dispositivo: number | string;
    nombre: string | null;
    patente: string | null;
    url_icono: string;
    url_imagen: string;
    id_deposito: string | null;
    id_deposito_fin: string | null;
    codigo_erp: string | null;
    telefono: string | null;
    chofer: string | null;
    expiracion_token: string | null;
    utilizacion_token: string | null;
    nombre_chofer: string | null;
    rateId: number | null;
    zonas: Array<string> | null;
    cortes: Record<string, IDevicesCuts> | null;
    startTime?: number;
    minTime?: number;
    vueltaDeposito: number;
    fixedCost?: NullableStringOrNumber;
    color: string | null;
    descarga: number;
    token_expirado: boolean;
    estado: string;
    parametros: IDeviceParameters[] | null;
    optimized: boolean;
    enabled: boolean;
    maxTime?: number;
    maxDistance?: number;
    optimizedStartTime?: boolean;
    kmCost?: NullableStringOrNumber;
    hourCost?: NullableStringOrNumber;
    fixedCostIncludedStops?: NullableStringOrNumber;
    stopCost?: NullableStringOrNumber;
    agrupacion?: string | null;
}

export interface IDevicesCuts {
    id_maquina: number | string;
    valor: number | string;
    nombre: string;
    url_icono: string;
    unidad: number | string;
    prefijo: string;
    sufijo: string;
    carga: number | string;
    nombreOriginal: string;
}

export interface IDeviceParameters {
    codigo_parametro: string;
    valor: string;
}
export interface IOrders {
    id: string;
    id_dispositivo_asignado: string | null;
    fecha_asignacion: string | null;
    fecha_pedido: string;
    fecha_entrega_desde: string | null;
    fecha_entrega_hasta: string | null;
    horario_entrega_desde: string | null;
    horario_entrega_hasta: string | null;
    codigo_pedido: string;
    priority: string;
    operacion: string;
    orden: string | null;
    id_cliente: string;
    codigo: string;
    razon_social: string;
    domicilio: string;
    latitud: string | null;
    longitud: string | null;
    codigo_tipo_cliente: string | null;
    observaciones: string | null;
    time_window: string | null;
    dia_visita: string | null;
    estado_cliente: string | null;
    id_dispositivo_cliente: string | null;
    url_icon: string | null;
    tipo_cliente: string | null;
    agrupacion: string | null;
    agrupacion_2: string | null;
    agrupacion_3: string | null;
    comentarios: string | null;
    reintentos: number;
    cortes: IPedidosCortes[] | [];
    parametros: IPedidosParametros | null;
    peso_pedido: string | null;
    cobros: string | null;
    detallescheck: string | null;
    id_cliente_original: string;
    ungroup: number;
    demora: string | null;
    tw_object: [string, string][] | null;
    editar: boolean;
    status: string | null;
    tempAssigned?: boolean;
    tempDeviceId?: string | null;
    tempChecked?: boolean;
}

export interface IPedidosParametros {
    [key: string]: string | undefined;
    UNGROUP?: '0' | '1';
}

export interface IPedidosCortes {
    id_corte: number | string;
    nombre: string;
    valor: number | string;
    sufijo: string;
    prefijo: string;
}

export interface IZones {
    id: number;
    nombre: string | null;
    codigo: string | null;
    color: string | null;
    dia: string | null;
    codigo_tipo_zona: string | null;
    tipo_zona_descripcion: string | null;
    tipo_zona_id: string | null;
    detalle: string[] | null;
    dispositivos: string[] | null;
}

export interface IOptimizerStatus {
    internal_stage: string;
    stage: string;
    substage: string | null;
    progress: string | number;
    cost: string | number | boolean;
    vehicles: string | number | boolean;
    solution: string | null;
    step?: string | null | boolean;
    total_steps?: string | null | boolean;
    status?: {
        breaches: {
            capacity: string | false;
            timewindow: string | false;
            minTime: string | false;
            maxTime: string | false;
            waits: number | false;
        };
        goal: string | false;
        duration: string | false;
        distance: string | false;
    };
}

export interface ICarga {
    pedidos: string[];
    pedidosOrdenados: string[];
    summary?: {
        travelTime: number;
        distance: number;
    };
}

export interface IOptimizerResult {
    routeObj: any;
    asignacion: Record<string, IOptimizerResultAsignacion> | null;
    recurrente: number;
    descripcion: string;
    cost: number;
    status: number;
    gap: number;
}

export interface IOptimizerResultAsignacion {
    pedidos: IOptimizerAsignacionPedidos[];
    pedidosOrdenados: IOptimizerAsignacionPedidos[];
    startTime: IOptimizerAsignacionHora;
    deviceValues?: {
        distance: string | number;
        time: string | number;
    };
}

export interface IOptimizerAsignacionPedidos {
    tipo: string;
    id: number;
    hora: IOptimizerAsignacionHora;
}

export interface IOptimizerAsignacionHora {
    horas: number;
    minutos: number;
}

export interface IOptimizerParameters {
    objective: string;
    performance: string;
    grouping: string;
    tolerance: string;
    zones: string;
    warehouse: string[];
    clearPreviousRoutes: boolean;
    onlyEmptyVehicles: boolean;
    calculateRouteStart: boolean;
    avoidTolls: boolean;
}

type ErrorInfo = Record<string, string>;

export type ImportResult = [AmountOfOrders, SuccesInfo, ErrorInfo];

export type UserParameters = {
    TIENDA_NUBE_TOKEN: string;
    [key: string]: string;
};

export type LoadSummary = {
    clientes?: number;
    pedidos?: number;
    Peso?: number;
    Volumen?: number;
    Dinero?: number;
    distancia?: number;
    tiempo?: number;
    tiempoTotal?: number;
};

// TODO: move to chameleon modals
export type OptimizationResult = {
    asignacion: Record<string, IOptimizerResultAsignacion> | null;
    summary: LoadSummary;
};
